import React, { useState, useEffect, useMemo } from "react";
import TitlePages from "../../layout/TitlePages";
import Breadcrumbs from "../../layout/Breadcrumbs";
import { EnCorto } from "../../data/EnCorto/dataEnCorto";
import "./EnCorto.css";

// Icono Play centrado
const PlayIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

// Icono de presentador / expositor
const SpeakerIcon = () => (
  <svg
    className="encorto-speaker-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const EnCortoComponent = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 9; // Cuadrícula fija 3 x 3 (9 videos por página)

  useEffect(() => {
    document.title = `En Corto`;
  }, []);

  // Cerrar modal con tecla Esc
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isModalOpen) {
        setIsModalOpen(false);
        setSelectedVideoUrl(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  const openModal = (facebookWatchUrl) => {
    const embedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
      facebookWatchUrl
    )}&show_text=0&autoplay=1`;
    setSelectedVideoUrl(embedUrl);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideoUrl(null);
  };

  // Filtrado reactivo en tiempo real
  const filteredVideos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return EnCorto;
    return EnCorto.filter(
      (item) =>
        (item.titleItem && item.titleItem.toLowerCase().includes(term)) ||
        (item.description && item.description.toLowerCase().includes(term))
    );
  }, [searchTerm]);

  const totalItems = filteredVideos.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Videos paginados (3 x 3 = 9 por página)
  const paginatedVideos = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredVideos.slice(start, start + PAGE_SIZE);
  }, [filteredVideos, currentPage]);

  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <>
      <Breadcrumbs
        path={[{ label: "Comunicación Social" }, { label: "En Corto" }]}
      />
      <TitlePages title="Comunicación Social" subTitle="En Corto" />

      <div className="encorto-wrapper">
        {/* Barra superior con contador y buscador */}
        <div className="encorto-toolbar">
          <div className="encorto-badge-counter">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
              <line x1="7" y1="2" x2="7" y2="22" />
              <line x1="17" y1="2" x2="17" y2="22" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <line x1="2" y1="7" x2="7" y2="7" />
              <line x1="2" y1="17" x2="7" y2="17" />
              <line x1="17" y1="17" x2="22" y2="17" />
              <line x1="17" y1="7" x2="22" y2="7" />
            </svg>
            <span>{totalItems} {totalItems === 1 ? "Video disponible" : "Videos disponibles"}</span>
          </div>

          <div className="encorto-search-box">
            <svg
              className="encorto-search-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="encorto-search-input"
              placeholder="Buscar por tema o expositor..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Buscar videos de En Corto"
            />
            {searchTerm && (
              <button
                type="button"
                className="encorto-clear-btn"
                onClick={() => setSearchTerm("")}
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Cuadrícula de 4 x 4 */}
        {paginatedVideos.length === 0 ? (
          <div className="encorto-empty">
            No se encontraron videos que coincidan con &ldquo;{searchTerm}&rdquo;.
          </div>
        ) : (
          <div className="encorto-grid">
            {paginatedVideos.map((item) => (
              <div
                key={item.id}
                className="encorto-card"
                onClick={() => openModal(item.facebookUrl)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openModal(item.facebookUrl);
                  }
                }}
                aria-label={`Reproducir video: ${item.titleItem}`}
              >
                {/* Miniatura 16:9 con Play */}
                <div className="encorto-thumbnail-box">
                  <img
                    src="https://itetlax.org.mx/assets/img/encorto.png"
                    alt={`Miniatura de ${item.titleItem}`}
                    className="encorto-thumbnail-img"
                    loading="lazy"
                  />

                  {/* Badge de nuevo video */}
                  {item.nuevo && (
                    <span className="encorto-badge-new">
                      ★ Nuevo video
                    </span>
                  )}

                  {/* Capa con botón Play */}
                  <div className="encorto-thumbnail-overlay">
                    <div className="encorto-play-circle" aria-hidden="true">
                      <PlayIcon />
                    </div>
                    <span className="encorto-play-label">Reproducir video</span>
                  </div>
                </div>

                {/* Cuerpo de la tarjeta: TÍTULO ELEGANTE OSCURO (NO ROSA) */}
                <div className="encorto-card-body">
                  <h3 className="encorto-card-title">{item.titleItem}</h3>
                  {item.description && (
                    <p className="encorto-card-desc">
                      <SpeakerIcon />
                      <span>{item.description}</span>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginación limpia: 3 x 3 (9 por página) */}
        {totalItems > 0 && (
          <div className="encorto-pagination-bar">
            <div className="encorto-pagination-info">
              Mostrando {startIndex}–{endIndex} de {totalItems} videos
            </div>

            <div className="encorto-pagination-controls">
              <button
                type="button"
                className="encorto-page-btn"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                title="Primera página"
                aria-label="Primera página"
              >
                «
              </button>
              <button
                type="button"
                className="encorto-page-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title="Página anterior"
                aria-label="Página anterior"
              >
                ‹
              </button>

              <span className="encorto-page-btn is-active">
                {currentPage} / {totalPages}
              </span>

              <button
                type="button"
                className="encorto-page-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                title="Página siguiente"
                aria-label="Página siguiente"
              >
                ›
              </button>
              <button
                type="button"
                className="encorto-page-btn"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                title="Última página"
                aria-label="Última página"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal con iframe de Facebook y desenfoque de fondo */}
      {isModalOpen && (
        <div
          className="encorto-modal-backdrop"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Reproductor de video En Corto"
        >
          <div
            className="encorto-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="encorto-modal-close-btn"
              onClick={closeModal}
              title="Cerrar video"
              aria-label="Cerrar video"
            >
              ✕
            </button>
            <div className="encorto-modal-ratio">
              <iframe
                src={selectedVideoUrl}
                title="Video de Facebook"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                className="encorto-modal-iframe"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnCortoComponent;
